import {
    useCallback,
    useMemo,
    useState,
    type Dispatch,
    type SetStateAction,
} from "react";
import { Entity } from "@common/types/entity";
import type { CounterpartyComboboxValue } from "@components/transactions/TransactionSplitV2/CounterpartyCombobox";
import type { CreateNativeInvoiceFormStateResult } from "./createNativeInvoiceFormState";
import { lineCodingBaselineFromLines } from "./buildNativeInvoiceLineAccounting";
import { buildNativeInvoiceDirtyFingerprint } from "./buildNativeInvoiceDirtyFingerprint";
import { buildNativeInvoiceDraftBody } from "./buildNativeInvoiceDraftBody";
import { computeNativeInvoiceTotals } from "./computeNativeInvoiceTotals";
import {
    customerFormFieldsFromCounterparty,
    type NativeInvoiceCustomerBooks,
} from "./copyNativeInvoicePartyDetails";
import {
    defaultLine,
    parseRecipientList,
    withCustomerDefaultRecipients,
    withCustomerEmailInTo,
} from "./nativeInvoiceFormHelpers";
import {
    NativeInvoiceFormRawState,
    NativeInvoiceFormState,
    NativeInvoiceLineCodingByLineId,
    NativeInvoiceLineRow,
} from "./nativeInvoiceFormTypes";
import {
    applyResolvedFormState,
    resolveFormState,
    type ResolveFormStateContext,
} from "./resolveNativeInvoiceFormState";

interface UseNativeInvoiceFormParams {
    // Fully built initial state from `createNativeInvoiceFormState`. The parent
    // only mounts this hook once create/edit inputs are ready.
    initial: CreateNativeInvoiceFormStateResult;
    // Live suggestion for create (and entity switches). May briefly hold the
    // previous entity's suggestion during refetch; override-null display uses
    // a stable copy so the field does not blank.
    suggestedInvoiceNumber?: string;
    defaultEntity?: Entity;
    currentUserName?: string | null;
    currentUserEmail?: string | null;
    primaryAccrualLedgerId?: string;
    incomeAccountLedgerId?: string;
    // Drives template-specific payload normalization in the draft body (the
    // invoice/due dates are discarded server-side for templates — see
    // buildNativeInvoiceDraftBody).
    isTemplateMode?: boolean;
}

// Why a hand-rolled hook instead of useFormik?
// 1. Validation is fully server-side via the zod render-snapshot schema, so
//    we don't need Formik's field-level error/touched plumbing.
// 2. Submit is driven by a memoized `draftBody` (the API payload), not by
//    Formik's handleSubmit.
// 3. The line-items array needs imperative add/remove/update helpers; doing
//    this with Formik's FieldArray adds boilerplate without buying anything.
export function useNativeInvoiceForm({
    initial,
    suggestedInvoiceNumber,
    defaultEntity,
    currentUserName,
    currentUserEmail,
    primaryAccrualLedgerId,
    incomeAccountLedgerId,
    isTemplateMode,
}: UseNativeInvoiceFormParams) {
    const [raw, setRaw] = useState<NativeInvoiceFormRawState>(
        () => initial.raw,
    );
    const [lineCodingBaselineByLineId, setLineCodingBaselineByLineId] =
        useState<NativeInvoiceLineCodingByLineId>(
            () => initial.lineCodingBaselineByLineId,
        );

    // Hold the last defined suggestion in state (not a ref) so a refetch gap
    // after entity switch cannot blank an override-null invoice number.
    const [stableSuggestion, setStableSuggestion] = useState(
        suggestedInvoiceNumber ?? "",
    );
    if (
        suggestedInvoiceNumber != null &&
        suggestedInvoiceNumber !== stableSuggestion
    ) {
        setStableSuggestion(suggestedInvoiceNumber);
    }

    // Pins the invoice number once a save has persisted one (see
    // `lockInvoiceNumber` below): the live suggestion is derived from the
    // workspace's last-invoice-number query, which the save itself just
    // invalidated, so it resolves to a HIGHER number on the next render.
    // Without pinning, an untouched (override-null) invoice number would
    // keep re-deriving from that ever-increasing live suggestion, so every
    // subsequent save on the SAME draft would persist a newly incremented
    // number instead of the one already saved.
    //
    // Reset (adjust-during-render, same pattern useNativeInvoiceSave uses for
    // its own entity-switch reset) whenever the entity changes: a new From
    // entity starts a fresh, unsaved draft — see useNativeInvoiceSave, which
    // drops its cached invoiceId on the same entityId change — so it must
    // keep taking live suggestions instead of staying pinned to the previous
    // entity's saved number.
    const [lockedInvoiceNumber, setLockedInvoiceNumber] = useState<
        string | null
    >(null);
    const [prevLockEntityId, setPrevLockEntityId] = useState(defaultEntity?.id);
    if (defaultEntity?.id !== prevLockEntityId) {
        setPrevLockEntityId(defaultEntity?.id);
        if (lockedInvoiceNumber !== null) {
            setLockedInvoiceNumber(null);
        }
    }

    const effectiveSuggestedInvoiceNumber =
        lockedInvoiceNumber ?? suggestedInvoiceNumber ?? stableSuggestion;

    const resolveContext: ResolveFormStateContext = {
        suggestedInvoiceNumber: effectiveSuggestedInvoiceNumber,
        currentUserName,
        currentUserEmail,
        defaultEntity,
        isTemplateMode,
    };

    const state = resolveFormState(raw, resolveContext);

    // Public setState speaks in resolved `NativeInvoiceFormState`; the resolve
    // module maps each resolved change back onto the raw overrides.
    const setState: Dispatch<SetStateAction<NativeInvoiceFormState>> =
        useCallback(
            (action) => {
                setRaw((prevRaw) => {
                    const context: ResolveFormStateContext = {
                        suggestedInvoiceNumber: effectiveSuggestedInvoiceNumber,
                        currentUserName,
                        currentUserEmail,
                        defaultEntity,
                        isTemplateMode,
                    };
                    const prev = resolveFormState(prevRaw, context);
                    const next =
                        typeof action === "function" ? action(prev) : action;
                    return applyResolvedFormState({ prevRaw, prev, next });
                });
            },
            [
                effectiveSuggestedInvoiceNumber,
                currentUserName,
                currentUserEmail,
                defaultEntity,
                isTemplateMode,
            ],
        );

    const draftLineCodingBaseline = useMemo(
        () => lineCodingBaselineFromLines(state.lines),
        [state.lines],
    );

    // Fingerprint of the RAW state (not the resolved/derived state) so async
    // defaults never make the form look dirty. Drives the close-confirmation
    // guard; kept separate from the save-dedup hash (see useNativeInvoiceSave).
    const dirtyFingerprint = useMemo(
        () => buildNativeInvoiceDirtyFingerprint(raw),
        [raw],
    );

    const { subtotal, total, taxAmount, discountAmount, depositAmount } =
        useMemo(
            () =>
                computeNativeInvoiceTotals({
                    lines: state.lines,
                    tax: state.tax,
                    taxMode: state.taxMode,
                    discount: state.discount,
                    discountMode: state.discountMode,
                    deposit: state.deposit,
                    depositMode: state.depositMode,
                    isTaxPerLineItem: state.isTaxPerLineItem,
                    isDiscountPerLineItem: state.isDiscountPerLineItem,
                    isDepositPerLineItem: state.isDepositPerLineItem,
                }),
            [
                state.lines,
                state.tax,
                state.taxMode,
                state.discount,
                state.discountMode,
                state.deposit,
                state.depositMode,
                state.isTaxPerLineItem,
                state.isDiscountPerLineItem,
                state.isDepositPerLineItem,
            ],
        );

    const customerEmail = state.customer?.email ?? "";
    const replyToEmail = state.replyToEmail;
    const replyToName = state.replyToName;
    const toEmails = useMemo(
        () => parseRecipientList(state.emailTo),
        [state.emailTo],
    );
    const ccEmails = useMemo(
        () => parseRecipientList(state.emailCc, toEmails),
        [state.emailCc, toEmails],
    );
    const bccEmails = useMemo(
        () => parseRecipientList(state.emailBcc, [...toEmails, ...ccEmails]),
        [state.emailBcc, toEmails, ccEmails],
    );
    const fromName = state.from.businessName;
    const fromEmail = state.from.email;

    const applyTemplateFormState = useCallback(
        (next: CreateNativeInvoiceFormStateResult) => {
            setRaw((prev) => ({
                ...next.raw,
                invoiceNumberOverride: prev.invoiceNumberOverride,
            }));
            setLineCodingBaselineByLineId(next.lineCodingBaselineByLineId);
        },
        [],
    );

    const onSelectCustomer = (
        counterparty: CounterpartyComboboxValue | undefined,
        books?: NativeInvoiceCustomerBooks,
    ) => {
        setRaw((prev) => {
            const { customer, isSameCustomer, toFullName, toTaxId, toCompany } =
                customerFormFieldsFromCounterparty({
                    counterparty,
                    existingCustomer: prev.customer,
                    contact: books?.contact,
                    address: books?.address,
                });
            const emailTo = withCustomerEmailInTo({
                emailTo: prev.emailTo,
                previousCustomerEmail: prev.customer?.email,
                nextCustomerEmail: customer?.email,
            });
            const emailCc = withCustomerDefaultRecipients({
                current: prev.emailCc,
                previousDefaults: prev.customer?.cc,
                nextDefaults: customer?.cc,
            });
            const emailBcc = withCustomerDefaultRecipients({
                current: prev.emailBcc,
                previousDefaults: prev.customer?.bcc,
                nextDefaults: customer?.bcc,
            });
            // Same id (incl. both undefined): merge contact fields only. Do not
            // wipe toFullName / toTaxId on a no-op reselect. Re-seed To only
            // when the customer (or their email) actually changed, so removing
            // the default tag is not undone by an unrelated contact edit.
            if (isSameCustomer) {
                const previousEmail = prev.customer?.email ?? "";
                const nextEmail = customer?.email ?? "";
                if (previousEmail.toLowerCase() === nextEmail.toLowerCase()) {
                    return { ...prev, customer, emailCc, emailBcc };
                }
                return { ...prev, customer, emailTo, emailCc, emailBcc };
            }
            return {
                ...prev,
                customer,
                toFullName,
                toTaxId,
                toCompany,
                emailTo,
                emailCc,
                emailBcc,
            };
        });
    };

    const draftBody = useMemo(
        () =>
            buildNativeInvoiceDraftBody({
                state,
                defaultEntity,
                primaryAccrualLedgerId,
                incomeAccountLedgerId,
                lineCodingBaselineByLineId,
                isTemplateMode,
            }),
        [
            state,
            defaultEntity,
            primaryAccrualLedgerId,
            incomeAccountLedgerId,
            lineCodingBaselineByLineId,
            isTemplateMode,
        ],
    );

    return {
        state,
        setState,
        // True once the user has typed an explicit invoice number (the raw
        // override is set). `lockInvoiceNumber` (below) does NOT set this —
        // it only pins the derived value — so callers that need "has the
        // user touched this field" (e.g. to decide whether an entity switch
        // may clear it back to a fresh suggestion) must check this instead
        // of comparing `state.invoiceNumber` to the live suggestion prop,
        // which no longer matches once locked.
        hasInvoiceNumberOverride: raw.invoiceNumberOverride !== null,
        // Pins the resolved invoice number to `invoiceNumber`, ignoring the
        // live suggestion until the entity changes (see above). Call once a
        // save has persisted a real invoice number for the current draft.
        lockInvoiceNumber: (invoiceNumber: string) => {
            setLockedInvoiceNumber(invoiceNumber);
        },
        draftBody,
        draftLineCodingBaseline,
        dirtyFingerprint,
        subtotal,
        total,
        taxAmount,
        discountAmount,
        depositAmount,
        customerEmail,
        replyToEmail,
        replyToName,
        toEmails,
        ccEmails,
        bccEmails,
        fromName,
        fromEmail,
        onSelectCustomer,
        applyTemplateFormState,
        acknowledgeSavedLineCoding: (
            savedBaseline: NativeInvoiceLineCodingByLineId,
        ) => {
            setLineCodingBaselineByLineId(savedBaseline);
        },
        addLine: () =>
            setRaw((prev) => ({
                ...prev,
                lines: [...prev.lines, defaultLine()],
            })),
        // A single-line invoice can't drop to zero lines, so "deleting"
        // the last remaining line clears it back to a blank default line
        // (keeping its id) instead of removing it.
        removeLine: (lineId: string) =>
            setRaw((prev) => ({
                ...prev,
                lines:
                    prev.lines.length === 1
                        ? prev.lines.map((line) =>
                              line.id === lineId
                                  ? { ...defaultLine(), id: line.id }
                                  : line,
                          )
                        : prev.lines.filter((line) => line.id !== lineId),
            })),
        updateLine: (lineId: string, patch: Partial<NativeInvoiceLineRow>) =>
            setRaw((prev) => ({
                ...prev,
                lines: prev.lines.map((line) =>
                    line.id === lineId ? { ...line, ...patch } : line,
                ),
            })),
    };
}
